// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { FORMID, TestRunParams } from "../TestRun/TestRunConstants";
import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, { memo, useCallback } from "react";
import { Theme, createStyles, makeStyles, useTheme } from "@material-ui/core";
import { updateSymptomsList, useTestRunState } from "store/testrun";
import { useHistory, useParams } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import QuestionnaireForm from "../ui/Questionnaire/Questionnaire";
import QuestionnaireInput from "components/ui/Questionnaire/QuestionnaireInput";
import { SYMPTOMS } from "./SymptomsConstants";
import { confirmAlert } from "../../utils/confirmAlert";
import { cx } from "style/utils";
import { setStepReady } from "components/TestRun/TestRunStepReadyEmitter";
import { useAppDispatch } from "store";
import { useTestRunConfig } from "components/TestRun/useTestRunConfig";

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    question: {
      fontWeight: "bold",
      marginBottom: "0 !important",
    },
    questionSubtext: {
      marginBottom: "1em",
    },
    questionColumm: {
      paddingTop: "0",
      paddingBottom: "0",
    },
  })
);

export default () => {
  const styles = useStyle();
  const history = useHistory();
  const { step, testRunUID } = useParams<TestRunParams>();
  const { getNextDefaultStep, getNextStepOfType } = useTestRunConfig();
  const QuestionnaireFormMemoized = memo(QuestionnaireForm);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const testRunState = useTestRunState();

  const stepReadyCallback = useCallback(
    (stepReady: boolean) => {
      setStepReady(step, stepReady);
    },
    [step]
  );

  const reportedSymptoms = testRunState?.testRun?.reportedSymptoms || {};

  return (
    <QuestionnaireFormMemoized
      formId={FORMID}
      onFormReady={stepReadyCallback}
      onSubmit={async (event, questions) => {
        try {
          event.preventDefault();
          const updatedSymptoms: any = {};
          Object.getOwnPropertyNames(questions).forEach(fieldname => {
            if (questions[fieldname].value === "true") {
              updatedSymptoms[fieldname] = reportedSymptoms[fieldname] || {};
            }
          });
          await dispatch(
            updateSymptomsList({ reportedSymptoms: updatedSymptoms })
          );

          // Resets the state.
          stepReadyCallback(false);

          // If no symptoms are checked, go directly to the next step.
          const nextStep =
            Object.getOwnPropertyNames(updatedSymptoms).length > 0
              ? getNextDefaultStep({ currentStepName: step! })
              : getNextStepOfType({
                  currentStepName: step!,
                  nextStepType: "noSymptoms",
                });

          const path = getPath(ROUTE_DEFINITIONS.PERFORMTEST, {
            params: {
              ":testRunUID": testRunUID,
              ":step": nextStep!,
            },
          });
          history.push(path);
        } catch (e) {
          confirmAlert({ title: "oops", message: e.message, theme });
        }
      }}
    >
      <div>
        <div className={cx({ [styles.question]: true })}>
          Which of the following were present during your illness?
        </div>
        <div className={styles.questionSubtext}>
          <i>Select all that apply</i>
        </div>
        <Grid container spacing={2}>
          {SYMPTOMS.map(symptom => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              className={styles.questionColumm}
              key={symptom.name + "_check"}
            >
              <QuestionnaireInput
                label={symptom.label}
                mandatory={false}
                name={symptom.name}
                type="checkbox"
                value={!!reportedSymptoms[symptom.name] + ""}
              />
            </Grid>
          ))}
        </Grid>
      </div>
    </QuestionnaireFormMemoized>
  );
};
